// src/pages/RequestApprovalPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getDatabase, ref, push, set, get, remove
} from "firebase/database";
import { initializeApp, getApps } from "firebase/app";
import UElogo from "../assets/UElogo_horizontal.png";
import { ArrowBigLeft } from "lucide-react";
import { EMAILJS } from "../config/emailConfig";
import { notifyApprover } from "../utils/sendEmail";

// 🔒 Ensure Firebase is initialized here too (safe if already initialized elsewhere)
const firebaseConfig = {
  apiKey: "AIzaSyAaR9ZU1w_bFxMKMbNxu-9eBpuOOGm1Eow",
  authDomain: "bookingappv1-d1e2d.firebaseapp.com",
  databaseURL: "https://bookingappv1-d1e2d-default-rtdb.firebaseio.com/",
  projectId: "bookingappv1-d1e2d",
  storageBucket: "bookingappv1-d1e2d.appspot.com",
  messagingSenderId: "134532713601",
  appId: "1:134532713601:web:5ad89fec3f4bf09b519180",
};
if (!getApps().length) initializeApp(firebaseConfig);

// ---- Helpers ----
function toISO(dateStr, timeStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  return new Date(y, m - 1, d, hh, mm || 0, 0, 0).toISOString();
}
function formatYMDLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
/** All 30-min slots whose [slot, slot+30min) overlap [startISO,endISO) */
function halfHourSlotsOverlapping(startISO, endISO) {
  const start = new Date(startISO);
  const end = new Date(endISO);
  if (!(start < end)) return [];
  const out = [];
  const cur = new Date(start);
  // snap down to the previous half-hour
  cur.setSeconds(0, 0);
  const minutes = cur.getMinutes();
  cur.setMinutes(minutes < 30 ? 0 : 30);
  while (cur < end) {
    const s0 = new Date(cur);
    const s1 = new Date(cur);
    s1.setMinutes(s1.getMinutes() + 30);
    if (s1 > start && s0 < end) {
      out.push({
        dateKey: formatYMDLocal(s0),
        time: `${String(s0.getHours()).padStart(2, "0")}:${String(s0.getMinutes()).padStart(2, "0")}`,
      });
    }
    cur.setMinutes(cur.getMinutes() + 30);
  }
  return out;
}

export default function RequestApprovalPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState(null);

  const [room, setRoom] = useState(state?.room || "");
  const [name, setName] = useState(state?.requesterName || "");
  const [email, setEmail] = useState(state?.requesterEmail || "");
  const [beginDate, setBeginDate] = useState(state?.beginDate || "");
  const [beginTime, setBeginTime] = useState(state?.beginTime || "08:00");
  const [endDate, setEndDate] = useState(state?.endDate || "");
  const [endTime, setEndTime] = useState(state?.endTime || "09:00");
  const [motive, setMotive] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (u) => setAuthUser(u));
  }, []);

  const startISO = useMemo(() => toISO(beginDate, beginTime), [beginDate, beginTime]);
  const endISO   = useMemo(() => toISO(endDate, endTime), [endDate, endTime]);

  const goBack = () => navigate(-1);

  // 🔎 Check only for existing reservations (we re-check restricted/unavailable on approve)
  async function checkReservedConflicts(db, room, slots) {
    if (!slots.length) return { ok: false, reason: "Invalid period." };
    const roomSnap = await get(ref(db, `reservations/buildingA/${room}`));
    const roomData = roomSnap.exists() ? roomSnap.val() : {};
    const conflicts = [];
    for (const { dateKey, time } of slots) {
      const r = roomData?.[dateKey]?.[time];
      if (r && r.status !== "Removed") conflicts.push(`${dateKey} ${time}`);
    }
    return conflicts.length
      ? { ok: false, reason: conflicts.length === 1
        ? `The selected period overlaps a reserved slot: ${conflicts[0]}.`
        : `The selected period overlaps reserved slots: ${conflicts.join(", ")}.` }
      : { ok: true };
  }

  // ✅ Preflight: can we write to /approvalRequests?
  async function preflightWrite(db) {
    const testKey = `__preflight__/${Date.now()}`;
    const r = ref(db, `approvalRequests/${testKey}`);
    try {
      await set(r, { ok: true, at: Date.now() });
      await remove(r);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e };
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!authUser) { setError("Please log in."); return; }
    if (!room || !name || !email || !beginDate || !beginTime || !endDate || !endTime || !motive) {
      setError("Please fill in all fields."); return;
    }
    if (!(new Date(startISO) < new Date(endISO))) {
      setError("End must be after the beginning."); return;
    }

    setSubmitting(true);
    try {
      const db = getDatabase();

      // 🔧 Quick preflight to surface rules/DB issues immediately
      const pf = await preflightWrite(db);
      if (!pf.ok) {
        const msg = pf.error?.message || String(pf.error);
        setError(`Database write blocked: ${msg}`);
        setSubmitting(false);
        return;
      }

      const overlapped = halfHourSlotsOverlapping(startISO, endISO);

      // ❗ pre-check: already reserved?
      const reservedCheck = await checkReservedConflicts(db, room, overlapped);
      if (!reservedCheck.ok) {
        setError(reservedCheck.reason || "The selected period is not available.");
        setSubmitting(false);
        return;
      }

      // 📝 Create the approval request
      const reqRef = push(ref(db, "approvalRequests"));
      const approvalId = reqRef.key;
      const approvalLink = `${window.location.origin}/approvals/${approvalId}`;

      const payload = {
        status: "pending",
        room,
        requestedBy: { name, email, uid: authUser.uid },
        period: { startISO, endISO, slots: overlapped },
        motive,
        createdAt: new Date().toISOString(),
      };

      await set(reqRef, payload);

      // ✉️ Notify approver (Account A via helper). Don’t fail the request if email fails.
      try {
        await notifyApprover({
          room,
          requester_name: name,
          requester_email: email,
          begin_date: startISO,   // or `${beginDate} ${beginTime}` if your template expects that
          end_date: endISO,       // or `${endDate} ${endTime}`
          motive,
          approval_link: approvalLink,
        });
      } catch (mailErr) {
        console.error("notifyApprover failed:", mailErr);
        alert("Request saved, but email notification failed — the approver can still see it on the approvals page.");
      }

      alert("Request submitted. You will be notified after review.");
      navigate("/reservations/buildingA");
    } catch (err) {
      console.error("Submit error:", err);
      setError(`Failed to submit request: ${err?.code || ""} ${err?.message || String(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col p-6 pt-28 w-full min-h-screen bg-gray-800 text-gray-100">
      <div className="w-full flex justify-between items-center px-6 py-4 absolute top-0 left-0">
        <img src={UElogo} alt="UE Logo" className="h-12 w-auto" />
        <button onClick={goBack} className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded">
          <ArrowBigLeft className="w-5 h-5 text-white" />
        </button>
      </div>

      <h1 className="w-full mb-6 text-left px-6 text-xl font-bold">Request Reservation Approval</h1>

      <p className="text-gray-300 mb-4">
        By using this reservation system, you agree that your reservations are for academic purposes only and not for commercial use.
      </p>

      <p className="text-gray-300 mb-4">
        Don't forget to indicate any special requirements.
      </p>



      {/* Centered like ContactPage */}
      <div className="flex flex-col items-center justify-start bg-gray-800 text-gray-100 p-8">
        <form onSubmit={handleSubmit} className="w-full max-w-md space-y-4">
          <div>
            <label className="block mb-1 text-sm">Room</label>
            <input value={room} readOnly className="w-full p-2 rounded bg-gray-700 text-white" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 text-sm">Name</label>
              <input value={name} onChange={(e)=>setName(e.target.value)} required className="w-full p-2 rounded bg-gray-700 text-white" />
            </div>
            <div>
              <label className="block mb-1 text-sm">E-mail</label>
              <input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required className="w-full p-2 rounded bg-gray-700 text-white" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 text-sm">Begin (Date)</label>
              <input type="date" value={beginDate} onChange={(e)=>setBeginDate(e.target.value)} required className="w-full p-2 rounded bg-gray-700 text-white" />
            </div>
            <div>
              <label className="block mb-1 text-sm">Begin (Time)</label>
              <input
  type="time"
  step="1800"
  value={beginTime}
  onChange={(e) => setBeginTime(e.target.value)}
  required
  className="w-full p-2 rounded bg-gray-700 text-white"
/>
            </div>
            <div>
              <label className="block mb-1 text-sm">End (Date)</label>
              <input type="date" value={endDate} onChange={(e)=>setEndDate(e.target.value)} required className="w-full p-2 rounded bg-gray-700 text-white" />
            </div>
            <div>
              <label className="block mb-1 text-sm">End (Time)</label>
              <input
  type="time"
  step="1800"
  value={endTime}
  onChange={(e) => setEndTime(e.target.value)}
  required
  className="w-full p-2 rounded bg-gray-700 text-white"
/>
            </div>
          </div>

          <div>
            <label className="block mb-1 text-sm">Motive and other relevant specifications</label>
            <textarea rows={4} value={motive} onChange={(e)=>setMotive(e.target.value)} required className="w-full p-2 rounded bg-gray-700 text-white" />
          </div>

          {error && <p className="text-red-400">{error}</p>}

          <div className="flex justify-center">
            <button disabled={submitting} type="submit" className="bg-purple-700 hover:bg-purple-800 disabled:opacity-60 text-white px-4 py-2 rounded">
              {submitting ? "Submitting..." : "Submit Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

